import ApexCharts from "apexcharts";
import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy, ViewChild, ElementRef, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { CCModalService } from "src/app/services/performance-service/cc-modal.service";
import { Subscription } from "rxjs";
import { FindingDetailsModalService } from "src/app/services/performance-service/finding-details-modal.service";
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: "app-cc-modal",
  standalone: true,
  imports: [
    TranslateModule,CommonModule],
  templateUrl: "./cc-modal.component.html",
  styleUrls: ["./cc-modal.component.scss"],
})
export class CCModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Output() closeModal = new EventEmitter<void>();

  private findingDetailsModalService = inject(FindingDetailsModalService);

  @ViewChild("ccChartEl") ccChartEl!: ElementRef<HTMLDivElement>;
  @ViewChild("creChartEl") creChartEl!: ElementRef<HTMLDivElement>;

  creTableData: any[] = [];

  openFindingDetails(row: { location: string; findingNumber?: string }): void {
    const match = this.creTableData.find(
      r => r.finding_number === (row as any).findingNumber || r.location === row.location
    );
    if (match) {
      this.findingDetailsModalService.openWithCreRow(match);
    } else {
      this.findingDetailsModalService.open();
    }
  }

  private ccChart: ApexCharts | null = null;
  private creChart: ApexCharts | null = null;

  private ccModalService = inject(CCModalService);
  private dataSub: Subscription | null = null;

  ngOnChanges(changes: SimpleChanges) {
    if (changes["isOpen"]) {
      const ionContent = document.querySelector("ion-content.dashboard-wrapper") as HTMLElement;
      if (ionContent) {
        if (this.isOpen) {
          ionContent.style.setProperty("--overflow", "hidden");
        } else {
          ionContent.style.removeProperty("--overflow");
        }
      }
      if (this.isOpen) {
        setTimeout(() => this.subscribeAndBuild(), 0);
      } else {
        this.dataSub?.unsubscribe();
        this.dataSub = null;
        this.destroyCharts();
      }
    }
  }

  ngOnDestroy(): void {
    this.dataSub?.unsubscribe();
    this.destroyCharts();
  }

  close() {
    this.closeModal.emit();
  }

  private subscribeAndBuild(): void {
    this.dataSub?.unsubscribe();
    this.dataSub = this.ccModalService.modalData$.subscribe((data) => {
      if (data) {
        this.ccScore        = data.ccScore;
        this.ccChartData    = data.ccChartData;
        this.ccCard         = { ...this.ccCard, subtitle: data.ccSubtitle };
        this.ccTop3         = data.ccTop3;
        this.ccBottom3      = data.ccBottom3;
        this.ccVerification = data.ccVerification;
        this.creScore       = data.creScore;
        this.creChartData   = data.creChartData;
        this.creProgress    = data.creProgress;
        this.creStats       = data.creStats;
        this.overdueFindings = data.overdueFindings;
        this.creTableData    = data.creTableData ?? [];
        this.aiText          = data.aiText;
      }
      this.buildCharts();
    });
  }

  private buildCharts(): void {
    this.destroyCharts();

    if (this.ccChartEl?.nativeElement) {
      this.ccChart = new ApexCharts(
        this.ccChartEl.nativeElement,
        this.sparklineOptions(this.ccChartData, this.lineColor(this.ccScore))
      );
      this.ccChart.render();
    }

    if (this.creChartEl?.nativeElement) {
      this.creChart = new ApexCharts(
        this.creChartEl.nativeElement,
        this.sparklineOptions(this.creChartData, this.lineColor(this.creScore))
      );
      this.creChart.render();
    }
  }

  private destroyCharts(): void {
    this.ccChart?.destroy();
    this.ccChart = null;
    this.creChart?.destroy();
    this.creChart = null;
  }

  private sparklineOptions(data: number[], color: string): any {
    const max = data.length ? Math.max(...data) : 100;
    return {
      series: [{ name: "", data }],
      chart: {
        type: "line",
        height: 80,
        width: "100%",
        background: "transparent",
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: false },
        sparkline: { enabled: true },
      },
      stroke: { curve: "smooth", width: 1.5, colors: [color] },
      xaxis: {
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { show: false, min: 0, max: max * 1.45 },
      grid: { show: false, padding: { right: 36, left: 0, top: 8, bottom: 6 } },
      markers: {
        size: 2.5,
        colors: [color],
        strokeWidth: 0,
        discrete: [
          { seriesIndex: 0, dataPointIndex: 0, fillColor: "transparent", strokeColor: "transparent", size: 0 },
        ],
      },
      legend: { show: false },
      dataLabels: { enabled: false },
      tooltip: { enabled: false },
      colors: [color],
    };
  }

  lineColor(score: number): string {
    if (score < 55)   return "#9F0014";
    if (score < 85)   return "#FFDE72";
    if (score < 95)   return "#94C12E";
    if (score <= 100) return "#009146";
    return "#800080";
  }

  chartBg(score: number): string {
    if (score < 55)   return 'linear-gradient(166deg, rgb(245,229,231) 3%, rgb(245,229,231) 44%, rgb(236,206,209) 63%, rgb(236,206,209) 84%)';
    if (score < 85)   return 'linear-gradient(166deg, rgba(255,249,229,1) 3%, rgba(255,249,229,1) 44%, rgba(255,244,206,1) 63%, rgba(255,244,206,1) 84%)';
    if (score < 95)   return 'linear-gradient(166deg, rgba(244,249,234,1) 3%, rgba(244,249,234,1) 44%, rgba(234,243,215,1) 63%, rgba(234,243,215,1) 84%)';
    if (score <= 100) return 'linear-gradient(166deg, rgba(229,244,236,1) 3%, rgba(229,244,236,1) 44%, rgba(206,234,219,1) 63%, rgba(206,234,219,1) 84%)';
    return 'linear-gradient(166deg, rgba(239,229,245,1) 3%, rgba(239,229,245,1) 44%, rgba(225,206,236,1) 63%, rgba(225,206,236,1) 84%)';
  }

  // ── Header ────────────────────────────────────────────────────────────
  headerIconSrc = "assets/performance/critical-control.svg";
  headerTitle   = "Critical Controls";

  // ── AI Box ────────────────────────────────────────────────────────────
  aiText = '';

  // ── Chart Scores ──────────────────────────────────────────────────────
  ccScore  = 0;
  creScore = 0;

  // ── Chart Data ────────────────────────────────────────────────────────
  ccChartData:  number[] = [];
  creChartData: number[] = [];

  // ── Critical Controls Card ────────────────────────────────────────────
  ccCard = {
    iconSrc:  "assets/performance/critical-control.svg",
    iconAlt:  "CC",
    title:    "Critical Controls",
    subtitle: '',
  };

  ccTop3:    Array<{ name: string; score: string }> = [];
  ccBottom3: Array<{ name: string; score: string }> = [];
  ccVerification = '';

  // ── CRE Card ──────────────────────────────────────────────────────────
  creCard = {
    iconSrc:  "assets/performance/cre-shield.svg",
    iconAlt:  "CRE",
    title:    "Critical Risk Elimination",
    subtitle: "On-time finding closure",
  };

  // ── CRE Progress Bar ──────────────────────────────────────────────────
  creProgress = { closed: 0, open: 0, overdue: 0 };
  progressLegend = [
    { label: "Closed",  colorClass: "cc-progress-legend__dot--green" },
    { label: "Open",    colorClass: "cc-progress-legend__dot--yellow" },
    { label: "Overdue", colorClass: "cc-progress-legend__dot--red" },
  ];

  // ── CRE Stats ─────────────────────────────────────────────────────────
  creStats: Array<{ value: number; label: string }> = [];

  // ── Overdue CRE Findings ──────────────────────────────────────────────
  overdueTitle    = "Overdue CRE Findings:";
  overdueSubtitle = "Impacting country CIS";
  overdueColumns  = ["Date", "Location", "Level"];
  searchIconSrc   = "assets/performance/search-icon.svg";

  overdueFindings: Array<{ date: string; location: string; level: number; dateColor: string; findingNumber?: string }> = [];
}
